<script setup lang="ts">
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import {
	cancelClassModal,
	openAvailabilityModal,
} from "@/components/modals/modalDefinitions";
import { useSessionStore } from "@/store/session";
import { useCalendarStore, type ICalendarEvent } from "@/store/calendar";
import type { TTeacherCalendarFiltersType } from "@/assets/js/globalTypes";
import { useToastStore } from "@/store/toast";
import { useIsMobile } from "@/assets/js/helpers";
import MdiViewAgendaOutline from "~icons/mdi/view-agenda-outline";
import MdiFilterOutline from "~icons/mdi/filter-outline";
import MdiPlusCircleOutline from "~icons/mdi/plus-circle-outline";

import api from "@/api";
import {
	showAgendaPopover,
	showAvailabilityPopover,
	showTeacherFiltersPopover,
} from "@/components/popovers/popoverDefinitions";
import { $t } from "@/i18n";
import { useTeacherStore } from "@/store/teacher";

defineOptions({
	name: "teacher.calendar",
});
definePage({
	name: "teacher.calendar",
});

const teacherStore = useTeacherStore();
const sessionStore = useSessionStore();
const calendarStore = useCalendarStore();
const isMobile = useIsMobile();

const teacherId = sessionStore.userId;
// const schoolId = sessionStore.userData.scl_id;
// const showAvailabilityModal = ref<boolean>(false);
const agendaEvent = ref<any>(null);
const schoolIds = ref<number[]>([]);
const courseTitles = ref<string[]>([]);
// const hideView = ref<"week" | "month">("month");
// const isLoading = ref(true);
const teacherMenuOptions = [
	{
		prependIcon: MdiViewAgendaOutline,
		label: $t("app.teacher.dashboard.calendar.mobileMenuAgenda") as string,
		onClick: () =>
			showAgendaPopover({
				events: calendarStore.getAllEvents,
				onDeleteEvent: (e) => handleDeleteEvent(e),
			}),
	},
	{
		prependIcon: MdiFilterOutline,
		label: $t("app.teacher.dashboard.calendar.mobileMenuFilters") as string,
		onClick: () =>
			showTeacherFiltersPopover({
				filters: filters.value,
				onFilterChange: (e) => filterChange(e),
			}),
	},
	{
		prependIcon: MdiPlusCircleOutline,
		label: $t(
			"app.teacher.dashboard.calendar.mobileMenuAvailability",
		) as string,
		onClick: () =>
			showAvailabilityPopover({
				onSaveAvailability: () => fetchInfo(),
			}),
	},
];

const filters = ref<TTeacherCalendarFiltersType | null>(null);

async function getCalendarFilters(isInitial: boolean = true) {
	try {
		const res = await api.getCalendarFilters({ usr_id: teacherId });

		if (isInitial) {
			filters.value = res.data.data;

			const schools =
				filters.value?.schools?.map((school) => ({
					...school,
					color: calendarStore.schoolColors[school.scl_id]
						? calendarStore.schoolColors[school.scl_id].slice(0, -2)
						: "#BA4EA2",
					checked: false, // Set initial checked value to false
				})) || [];

			const course_titles =
				filters.value.course_titles?.map((course) => ({
					crs_name: course,
					checked: false, // Set initial checked value to false
				})) || [];

			teacherStore.teacherSelectedCalendarFilters = {
				schools,
				course_titles,
			};
		} else {
			const currentFilters = teacherStore.teacherSelectedCalendarFilters;

			const newSchools = res.data.data?.schools || [];
			const updatedSchools = newSchools.map((newSchool: any) => {
				const existing = currentFilters.schools?.find(
					(s) => s.scl_id === newSchool.scl_id,
				);
				return {
					...newSchool,
					color: existing?.color ?? "#BA4EA2",
					checked: existing?.checked ?? false,
				};
			});

			const newCourses = res.data.data?.course_titles || [];
			const updatedCourses = newCourses.map((newCourse: string) => {
				const existing = currentFilters.course_titles?.find(
					(c) => c.crs_name === newCourse,
				);
				return {
					crs_name: newCourse,
					checked: existing?.checked ?? false,
				};
			});

			teacherStore.teacherSelectedCalendarFilters = {
				schools: updatedSchools,
				course_titles: updatedCourses,
			};
		}
	} catch (error) {
		console.error(error);
	}
}

onMounted(async () => {
	setInterval(() => {
		if (todaysClasses.value) {
			agendaInfo();
		}
	}, 30_000); // 1 minute
	await fetchInfo(true);
});

const todaysClasses = computed(() => {
	// const events = calendarStore.getEventsForDate(
	// 	calendarStore.getActiveDate.format("YYYY-MM-DD"),
	// );
	const events = calendarStore.getEventsForDate(dayjs().format("YYYY-MM-DD"));
	if (events.length === 0) return;
	return events.sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));
});

const agendaWatcher = watch(
	() => todaysClasses.value,
	(newValue) => {
		if (newValue) {
			agendaInfo();
			agendaWatcher();
		}
	},
);

const handleDayClick = async (day: Dayjs) => {
	calendarStore.setActiveDate(day);
	await fetchInfo();
};

const handleNavClick = async (direction: number, event?: any) => {
	if (event) {
		if (event === "week") {
			calendarStore.setActiveDate(
				direction > 0
					? calendarStore.getActiveDate.add(7, "day")
					: calendarStore.getActiveDate.subtract(7, "day"),
			);
		} else if (event === "month") {
			calendarStore.setActiveDate(
				calendarStore.getActiveDate.add(direction, "month"),
			);
		}
		await fetchInfo();

		return;
	}
	calendarStore.setActiveDate(
		calendarStore.getActiveDate.add(direction, "month"),
	);
	await fetchInfo();
};

const agendaInfo = () => {
	if (dayjs().isAfter(dayjs(todaysClasses.value.at(-1).start), "minute")) {
		agendaEvent.value = null;
		return;
	}

	let dayIndex = 0;
	for (const event of todaysClasses.value) {
		if (dayjs().isAfter(dayjs(event.start), "minute")) {
			dayIndex++;
		} else {
			break;
		}
	}

	const nextEvent = todaysClasses.value[dayIndex];

	const diffInMinutes = Math.abs(
		dayjs().diff(dayjs(nextEvent.start), "minute"),
	);

	const hours = Math.floor(diffInMinutes / 60);
	const minutes = diffInMinutes % 60;

	let formattedTimeUntil = "";
	if (hours > 0) formattedTimeUntil += `${hours}h `;
	formattedTimeUntil += `${minutes}m`;

	agendaEvent.value = {
		startTime: dayjs(nextEvent.start).format("HH:mm"),
		endTime: dayjs(nextEvent.end).format("HH:mm"),
		universityName: nextEvent.schoolName,
		className: nextEvent.courseName ?? nextEvent.title,
		location: nextEvent.location,
		timeUntilEvent: formattedTimeUntil.trim(),
	};
};

const handleRefreshWeek = (start: string, end: string) => {
	calendarStore.setWeekStart(start);
	calendarStore.setWeekEnd(end);
	fetchInfo();
};

const handleRefreshDate = (date: string) => {
	const previousDate = calendarStore.getActiveDate;
	calendarStore.setActiveDate(dayjs(date));
	if (previousDate.isSame(calendarStore.getActiveDate, "month")) return;
	fetchInfo();
};

const handleRefreshView = (view: "week" | "month") => {
	calendarStore.setView(view);
	fetchInfo();
};

// REVIEW: Fix this
// the delay is here so that the calendar can render the new dates
// and thus set the new weekStart and weekEnd
// before the fetchInfo is called
// i might be retarded but i couldn't figure out a better way to do this atm
const fetchInfo = async (isInitial: boolean = false) => {
	await setTimeout(async () => {
		// isLoading.value = true;
		calendarStore.isLoadingCalendar = true;
		const dateObject = {
			from: "",
			to: "",
		};

		if (calendarStore.getView === "week") {
			dateObject.from = calendarStore.getWeekStart();
			dateObject.to = calendarStore.getWeekEnd();
		} else {
			dateObject.from = calendarStore.getActiveDate
				.startOf("month")
				.format("YYYY-MM-DD");
			dateObject.to = calendarStore.getActiveDate
				.endOf("month")
				.format("YYYY-MM-DD");
		}

		await calendarStore.fetchCalendarClasses(
			teacherId,
			{
				date_from: dateObject.from,
				date_to: dateObject.to,
				scl_ids: schoolIds.value,
				crq_titles: courseTitles.value,
			},
			calendarStore.getView,
		);
		await calendarStore.fetchTeacherAvailability(
			teacherId,
			{
				avl_start: dateObject.from,
				avl_end: dateObject.to,
			},
			calendarStore.getView,
		);

		await getCalendarFilters(isInitial);

		// isLoading.value = false;
		calendarStore.isLoadingCalendar = false;
	}, 100);
};

async function filterChange(e: TTeacherCalendarFiltersType) {
	schoolIds.value = e.schools.map((school) => school.scl_id);
	courseTitles.value = e.course_titles;

	await fetchInfo();
}

function dayClickHandler(day: string) {
	// calendarStore.setActiveDate(day);
	// fetchInfo();
	if (isMobile.value) {
		showAvailabilityPopover({
			onSaveAvailability: () => fetchInfo(),
			clickedDate: new Date(day),
		});
	} else {
		openAvailabilityModal(fetchInfo, new Date(day));
	}
}

watch(
	() => isMobile.value,
	() => {
		calendarStore.setDayClickHandler(dayClickHandler);
	},
);

onBeforeMount(() => {
	calendarStore.setIsAdmin(false);
	calendarStore.setDayClickHandler(dayClickHandler);
	calendarStore.setDeleteEventHandler(cancelClass);
	calendarStore.setIsPreview(false);
});

onBeforeUnmount(() => {
	calendarStore.setDayClickHandler(null);
	calendarStore.setDeleteEventHandler(null);
});

const toastStore = useToastStore();
function cancelClass(classObj: ICalendarEvent) {
	cancelClassModal({
		id: classObj.id,
		onBack: () => {
			fetchInfo();
		},
		onCancelClassRequest: async (classData: { id: number; clr_id: number }) => {
			try {
				const res = await api.cancelClass(classData);
				// classStore.removeClass(classObj);
				toastStore.openToastSuccess(res.data.message);
			} catch (err) {
				toastStore.openToastError(err.response?.data?.message || err.message);
			} finally {
				fetchInfo();
			}
		},
	});
}

// const callEditEventHandler = (event: ICalendarEvent) => {
// 	const editEventHandler = calendarStore.getEditEvent();
// 	if (editEventHandler) {
// 		editEventHandler(event);
// 	}
// };

const hasDeleteEventHandler = computed(() => {
	return !!calendarStore.getDeleteEventHandler();
});
const handleDeleteEvent = (event: ICalendarEvent) => {
	if (hasDeleteEventHandler.value) {
		calendarStore.getDeleteEventHandler()(event);
	}
};

const calendarColorValues = computed(() => {
	const res = [
		{
			label: $t("app.base.available"),
			color: "#f5f9ff",
		},
		{
			label: $t("app.base.unavailable"),
			color: "#dee3eb",
		},
	];
	if (calendarStore.getView === "month") {
		res.push({
			label: $t("app.base.partiallyAvailable"),
			color: "partial",
		});
	}
	return res;
});
</script>

<template>
	<div id="teacher-calendar-page">
		<div
			v-if="!isMobile"
			class="calendar-page-sidebar"
		>
			<modern-calendar
				:current-date="calendarStore.getCurrentDate"
				@day-click="handleDayClick"
				@nav-click="handleNavClick"
			/>
			<p-button
				:outlined="true"
				:pt="{
					root: {
						class: ['calendar-md-button', 'calendar-md-button-outlined'],
						style: 'text-transform: capitalize',
					},
				}"
				@click="openAvailabilityModal(fetchInfo)"
			>
				<i-mdi-calendar-check-outline />
				{{ $t("app.teacher.dashboard.calendar.availabilityBtn") }}
			</p-button>
			<agenda-card :event="agendaEvent" />
			<calendar-filters-teacher
				@filter-change="filterChange($event as TTeacherCalendarFiltersType)"
			/>
			<calendar-colors :calendar-colors="calendarColorValues" />
		</div>
		<div class="calendar-wrap">
			<nice-calendar
				@next="handleNavClick(1, $event)"
				@prev="handleNavClick(-1, $event)"
				@refresh:week="handleRefreshWeek"
				@refresh:date="handleRefreshDate"
				@refresh:view="handleRefreshView"
			/>
		</div>
		<mobile-menu
			v-if="isMobile"
			:menu-items="teacherMenuOptions"
		/>
	</div>
</template>

<style lang="scss" scoped>
#teacher-calendar-page {
	display: flex;
	padding-top: 20px;
	box-sizing: border-box;
	height: calc(100dvh - $nav-top-bar - 1px);
	overflow-y: auto;

	.calendar-page-sidebar {
		display: flex;
		flex-direction: column;
		gap: 10px;
		padding: 10px;
		box-sizing: border-box;
	}

	.calendar-wrap {
		flex: 1;
		box-sizing: border-box;
		height: 99%;
		width: calc(100vw - $mini-sidebar-width - 300px - 1px - 10px);
		padding: 10px 10px 0;
		overflow-y: auto;

		&::-webkit-scrollbar {
			display: none;
		}

		scrollbar-width: none;
		-ms-overflow-style: none;
	}
}

@keyframes bg-pulse {
	0%,
	100% {
		background-color: rgb(88 137 193 / 0.25);
	}
	50% {
		background-color: transparent;
	}
}

.animated-bg {
	animation: bg-pulse 2s infinite;

	&:hover {
		animation: unset;
	}
}
</style>
